/**
 * Позиции макета, которым карточка маркетплейса не достанется.
 *
 * Приёмка перестала падать на товаре без опубликованной контент-воронки:
 * файлы для печати ему делаются, а целей маркетплейса, визуалов и черновика
 * карточки у него не будет. Молча — значит человек об этом не узнаёт ниоткуда
 * (признак живёт в логе design-service и в requires_media = 0). Здесь ответ
 * приёмки превращается в одну понятную фразу для тоста.
 *
 * Показываем КАТЕГОРИИ, а не артикулы: у макета их до полутора десятков, а
 * решение («ладно, эти позиции производственные») принимается по категориям.
 */

export interface SkippedCategory {
  /** Префикс категории: PT7, PT01, PL4. */
  category: string
  /** Описание категории из PIM. */
  category_description?: string
  /** Сколько товаров этой категории осталось без карточки. */
  products?: number
  /** Код промаха резолвера. */
  code?: string
  /** Причина одной строкой по-русски. */
  reason?: string
  /** true — норма (воронки нет вовсе), false — похоже на сбой настройки. */
  expected?: boolean
  /** Кабинеты, в которых карточки не будет. */
  cabinets?: string[]
}

export interface FunnelCoverage {
  /** Заказывались ли в прогоне этапы маркетплейсов. */
  marketplace_stage: boolean
  covered_categories: string[]
  skipped_categories: SkippedCategory[]
  skipped_products: number
}

export interface CoverageNotice {
  title: string
  message: string
  /** info — норма, warning — похоже на недонастроенные воронки. */
  tone: 'info' | 'warning'
}

const DEFAULT_REASON = 'контент-воронка не подобрана'

/** Ответ приёмки → разбор покрытия; чужой/старый формат отдаёт null. */
export function parseFunnelCoverage(result: unknown): FunnelCoverage | null {
  const raw = (result as { funnel_coverage?: unknown } | null)?.funnel_coverage
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const skipped = Array.isArray(source.skipped_categories)
    ? (source.skipped_categories as SkippedCategory[]).filter(
        (item) => item && typeof item.category === 'string' && item.category,
      )
    : []
  return {
    marketplace_stage: source.marketplace_stage !== false,
    covered_categories: Array.isArray(source.covered_categories)
      ? (source.covered_categories as string[]).map(String)
      : [],
    skipped_categories: skipped,
    skipped_products: Number(source.skipped_products) || 0,
  }
}

/** Слияние разборов нескольких макетов — для массовой приёмки. */
export function mergeFunnelCoverage(
  items: Array<FunnelCoverage | null | undefined>,
): FunnelCoverage | null {
  const present = items.filter((item): item is FunnelCoverage => Boolean(item))
  if (present.length === 0) return null
  const byCategory = new Map<string, SkippedCategory>()
  for (const coverage of present) {
    for (const skipped of coverage.skipped_categories) {
      const current = byCategory.get(skipped.category)
      if (!current) {
        byCategory.set(skipped.category, { ...skipped })
        continue
      }
      byCategory.set(skipped.category, {
        ...current,
        products: (current.products || 0) + (skipped.products || 0),
        // Настоящий сбой информативнее ожидаемого пропуска — он и называет причину
        expected: current.expected !== false && skipped.expected !== false,
        reason: current.expected === false ? current.reason : skipped.reason ?? current.reason,
        code: current.expected === false ? current.code : skipped.code ?? current.code,
      })
    }
  }
  return {
    marketplace_stage: present.some((item) => item.marketplace_stage),
    covered_categories: [
      ...new Set(present.flatMap((item) => item.covered_categories)),
    ].sort((a, b) => a.localeCompare(b)),
    skipped_categories: [...byCategory.values()].sort((a, b) =>
      a.category.localeCompare(b.category),
    ),
    skipped_products: present.reduce((sum, item) => sum + (item.skipped_products || 0), 0),
  }
}

/** «PT7, PT01 — нет опубликованной контент-воронки; PL4 — …» */
export function skippedCategoriesLine(coverage: FunnelCoverage | null): string {
  const groups = new Map<string, string[]>()
  for (const skipped of coverage?.skipped_categories ?? []) {
    const reason = skipped.reason || DEFAULT_REASON
    groups.set(reason, [...(groups.get(reason) ?? []), skipped.category])
  }
  return [...groups.entries()]
    .map(([reason, categories]) => `${categories.join(', ')} — ${reason}`)
    .join('; ')
}

/**
 * Текст уведомления после приёмки. null — рассказывать не о чем.
 *
 * Норма и сбой различаются и тоном, и формулировкой: пропуск категории без
 * воронки — штатное поведение (позиция производственная), а вот воронка,
 * которая не нашлась при живой настройке, — повод сходить в раздел воронок.
 */
export function coverageNotice(
  coverage: FunnelCoverage | null,
  options: { designCode?: string; designs?: number } = {},
): CoverageNotice | null {
  if (!coverage || coverage.skipped_categories.length === 0) return null
  const misconfigured = coverage.skipped_categories.some((item) => item.expected === false)
  const scope = options.designs && options.designs > 1
    ? `Принято макетов: ${options.designs}.`
    : options.designCode
      ? `Макет #${options.designCode} принят.`
      : 'Макет принят.'
  const tail = misconfigured
    ? ' Проверьте настройку контент-воронок — часть категорий осталась без подходящей.'
    : ' Это норма: такие позиции идут как производственные — товары и файлы для печати им создаются.'
  return {
    tone: misconfigured ? 'warning' : 'info',
    title: misconfigured
      ? 'Часть категорий осталась без контент-воронки'
      : 'Карточку получат не все позиции макета',
    message: `${scope} Карточку маркетплейса не получат: ${skippedCategoriesLine(coverage)}.${tail}`,
  }
}
